<app-navbar></app-navbar>

<div class="saved-tests-layout">
  <a routerLink="/dashboard" class="back-link">
    <i class="bi bi-arrow-left"></i> Back to Dashboard
  </a>

  <h1 class="page-title">Saved Tests</h1>
  <p class="page-subtitle">Resume your saved tests or start a new one using a template.</p>

  <!-- Filter panel: All, Study, Exam, Filter by Subject - all in one panel -->
  <div class="panel filter-panel">
    <div class="filter-row">
      <div class="filter-group">
        <label class="filter-label">Test Mode</label>
        <div class="mode-buttons" role="group" id="modeSwitch">
          <input type="radio" class="btn-check" name="mode" id="modeAll" value="all" 
                 [(ngModel)]="selectedMode" (change)="onModeChange()" [checked]="selectedMode === 'all'">
          <label class="mode-btn" for="modeAll">All</label>
          <input type="radio" class="btn-check" name="mode" id="modeTest" value="test" 
                 [(ngModel)]="selectedMode" (change)="onModeChange()" [checked]="selectedMode === 'test'">
          <label class="mode-btn" for="modeTest">Study</label>
          <input type="radio" class="btn-check" name="mode" id="modeExam" value="exam" 
                 [(ngModel)]="selectedMode" (change)="onModeChange()" [checked]="selectedMode === 'exam'">
          <label class="mode-btn" for="modeExam">Exam</label>
        </div>
      </div>
      <div class="filter-group">
        <label class="filter-label" for="subjectFilter">Filter by Subject</label>
        <select class="form-select" id="subjectFilter" [(ngModel)]="selectedSubjectId" (change)="onSubjectChange()">
          <option [ngValue]="null">All Subjects</option>
          <option *ngFor="let subject of availableSubjects" [ngValue]="subject.subjectId">
            {{ subject.subjectTitle }}
          </option>
        </select>
      </div>
    </div>
  </div>

  <!-- Loading -->
  <div *ngIf="isLoading" class="text-center py-5">
    <div class="spinner-border" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
  </div>

  <!-- Content -->
  <div *ngIf="!isLoading">
    <div *ngIf="filteredTests.length === 0" class="panel">
      <p class="text-muted mb-0">No saved tests found for the selected filters.</p>
    </div>
    
    <div *ngIf="filteredTests.length > 0">
      <p class="table-count">Showing {{ filteredTests.length }} test{{ filteredTests.length === 1 ? '' : 's' }}</p>
      <div class="table-wrapper">
        <div class="table-responsive">
          <table class="table table-hover tests-table">
            <thead>
              <tr>
                <th (click)="onSort('subjectName')" class="sortable">Subject <i class="bi" [ngClass]="getSortIcon('subjectName')"></i></th>
                <th class="col-mode">Mode</th>
                <th (click)="onSort('questionCount')" class="sortable col-questions"><span class="header-full">Questions</span><span class="header-short">QU.</span> <i class="bi" [ngClass]="getSortIcon('questionCount')"></i></th>
                <th (click)="onSort('completed')" class="sortable col-completed"><span class="header-full">Completed</span><span class="header-short">COMPL.</span> <i class="bi" [ngClass]="getSortIcon('completed')"></i></th>
                <th (click)="onSort('score')" class="sortable">Score <i class="bi" [ngClass]="getSortIcon('score')"></i></th>
                <th (click)="onSort('savedDate')" class="sortable col-date">Saved Date <i class="bi" [ngClass]="getSortIcon('savedDate')"></i></th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let test of filteredTests" [attr.data-testid]="'saved-test-row-' + test.id">
                <td class="col-subject">
                  <div class="subject-cell">
                    <span>{{ test.subjectName }}</span>
                    <span *ngIf="test.subjectNames && test.subjectNames.length > 1" class="ms-1" [title]="getOtherSubjectsTooltip(test)">
                      <i class="bi bi-info-circle text-muted" aria-hidden="true"></i>
                    </span>
                    <span *ngIf="test.shared" class="shared-badge shared-badge-mobile">Shared</span>
                    <span class="mode-badge mode-badge-mobile" [class.mode-study]="test.mode === 'test'" [class.mode-exam]="test.mode === 'exam'">
                      {{ test.mode === 'exam' ? 'Exam' : 'Study' }}
                    </span>
                  </div>
                </td>
                <td class="col-mode">
                  <span class="mode-badge mode-badge-desktop" [class.mode-study]="test.mode === 'test'" [class.mode-exam]="test.mode === 'exam'">
                    {{ test.mode === 'exam' ? 'Exam' : 'Study' }}
                  </span>
                  <span *ngIf="test.shared" class="shared-badge shared-badge-desktop ms-1">Shared</span>
                </td>
                <td>{{ test.questionCount }}</td>
                <td>{{ formatCompleted(test.completedCount, test.questionCount) }}</td>
                <td>{{ formatScore(test.score) }}</td>
                <td class="col-date"><span class="date-full">{{ formatDate(test.savedDate) }}</span><span class="date-short">{{ formatDateShort(test.savedDate) }}</span></td>
                <td>
                  <div class="action-buttons">
                    <ng-container *ngIf="isTestFinished(test); else resumeBtn">
                      <button type="button" class="btn btn-primary btn-sm btn-recreate"
                              (click)="onRecreate(test.id)" 
                              [attr.data-testid]="'recreate-btn-' + test.id"
                              title="Recreate test with same settings">
                        <i class="bi bi-arrow-repeat btn-icon-only"></i> Recreate
                      </button>
                    </ng-container>
                    <ng-template #resumeBtn>
                      <button type="button" class="btn btn-primary btn-sm btn-resume"
                              (click)="onResume(test.id)" 
                              title="Resume">
                        <i class="bi bi-play-fill btn-icon-only"></i> Resume
                      </button>
                    </ng-template>
                    <button type="button" class="btn btn-sm btn-icon btn-icon-share"
                            [class.btn-icon-share-active]="test.shared"
                            (click)="openShareDialog(test)"
                            [title]="test.shared ? 'Test is shared — manage sharing' : 'Share test with another user'">
                      <i class="bi" [ngClass]="test.shared ? 'bi-share-fill' : 'bi-share'" class="btn-icon-only"></i><span class="btn-text-only">{{ test.shared ? 'Unshare' : 'Share' }}</span>
                    </button>
                    <span [title]="isTestFinished(test) ? 'View report' : 'No report available, test was not finished'" class="d-inline-block">
                      <button type="button" class="btn btn-sm btn-icon btn-icon-report"
                              (click)="onTestReport(test.id)" 
                              [disabled]="!isTestFinished(test)">
                        <i class="bi bi-file-text btn-icon-only"></i><span class="btn-text-only">Report</span>
                      </button>
                    </span>
                    <button type="button" class="btn btn-sm btn-icon btn-icon-delete" (click)="onDelete(test.id)" title="Delete">
                      <i class="bi bi-trash btn-icon-only"></i><span class="btn-text-only">Delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Share error dialog (e.g. no subscription) -->
<div *ngIf="shareErrorMessage !== null" class="share-dialog-overlay" (click)="closeShareError()"></div>
<div *ngIf="shareErrorMessage !== null" class="share-dialog" role="dialog" aria-labelledby="shareErrorLabel" aria-modal="true">
  <div class="share-dialog-content" (click)="$event.stopPropagation()">
    <div class="share-dialog-header">
      <h5 class="share-dialog-title" id="shareErrorLabel"><i class="bi bi-exclamation-triangle text-warning"></i> Cannot share</h5>
      <button type="button" class="btn-close" (click)="closeShareError()" aria-label="Close"></button>
    </div>
    <div class="share-dialog-body">
      <p class="share-dialog-note mb-0">{{ shareErrorMessage }}</p>
    </div>
    <div class="share-dialog-footer">
      <button type="button" class="btn btn-primary" (click)="closeShareError()">OK</button>
    </div>
  </div>
</div><!-- Share dialog: unified for share and stop-sharing -->
<div *ngIf="shareDialogTestId !== null" class="share-dialog-overlay" (click)="closeShareDialog()"></div>
<div *ngIf="shareDialogTestId !== null" class="share-dialog" role="dialog" aria-labelledby="shareDialogLabel" aria-modal="true">
  <div class="share-dialog-content">
    <div class="share-dialog-header">
      <h5 class="share-dialog-title" id="shareDialogLabel"><i class="bi bi-share"></i> Share test</h5>
      <button type="button" class="btn-close" (click)="closeShareDialog()" aria-label="Close"></button>
    </div>
    <div class="share-dialog-body">
      <ng-container *ngIf="shareDialogTest?.shared">
        <p class="share-dialog-note mb-0">This test is shared with <strong>{{ shareDialogTest?.sharedWithUsername || 'another user' }}</strong>.</p>
        <p class="share-dialog-note mb-0 mt-2">You can stop sharing at any time. The other user will no longer see this test in their Shared Tests.</p>
      </ng-container>
      <ng-container *ngIf="!shareDialogTest?.shared">
        <p class="share-dialog-note">Share this test with one of your accepted friends.</p>
        <div class="mb-3">
          <label for="shareFriend" class="form-label">Friend</label>
          <select
            id="shareFriend"
            class="form-select"
            data-testid="share-friend-select"
            [(ngModel)]="shareUsername"
            [disabled]="shareFriendsLoading || shareSubmitLoading">
            <option value="">Select a friend...</option>
            <option *ngFor="let friend of shareFriends" [value]="friend.displayName">
              {{ friend.displayName }}
            </option>
          </select>
          <div *ngIf="shareFriendsLoading" class="form-text">Loading friends...</div>
          <div *ngIf="!shareFriendsLoading && shareFriends.length === 0" class="form-text text-muted">
            No accepted friends yet. Add friends in Study together first.
          </div>
        </div>
      </ng-container>
    </div>
    <div class="share-dialog-footer">
      <button type="button" class="btn btn-secondary" (click)="closeShareDialog()">Cancel</button>
      <ng-container *ngIf="shareDialogTest?.shared">
        <button type="button" class="btn btn-warning" (click)="doStopSharing(); $event.stopPropagation()" [disabled]="shareStopLoading">
          <span *ngIf="shareStopLoading" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
          <span *ngIf="!shareStopLoading">Stop Sharing</span>
        </button>
      </ng-container>
      <ng-container *ngIf="!shareDialogTest?.shared">
        <button type="button" class="btn btn-primary" (click)="onShareSubmit(); $event.stopPropagation()"
                [disabled]="!shareUsername || shareSubmitLoading || shareFriends.length === 0">
          <span *ngIf="shareSubmitLoading" class="spinner-border spinner-border-sm" aria-hidden="true"></span>
          Share
        </button>
      </ng-container>
    </div>
  </div>
</div>
